import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import AuthContext from '../contexts/AuthContext';
import { Editor } from '@tinymce/tinymce-react';
const containerStyle = {
  padding: '1.5rem',
  display: 'flex',
  justifyContent: 'center',
  backgroundColor: '#f8f9fa'
};

const cardStyle = {
  backgroundColor: '#fff',
  padding: '2.5rem',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
  maxWidth: '700px',
  width: '100%',
  border: '1px solid #e9ecef',
  position: 'relative'
};

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: '#f8f9fa',
  padding: '1rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  zIndex: 1000,
  overflowY: 'auto'
};

const audioStyle = {
  width: '100%',
  margin: '1.5rem 0',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
};

const buttonStyle = {
  backgroundColor: '#667eea',
  color: '#fff',
  border: 'none',
  padding: '0.75rem 1.5rem',
  borderRadius: '8px',
  cursor: 'pointer',
  marginTop: '1rem',
  fontWeight: '600',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
};

const headerStyle = {
  textAlign: 'center',
  marginBottom: '2rem'
};

const TestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const audioRef = useRef(null);
  const editorRef = useRef(null);

  // State declarations
  const [audioUrl, setAudioUrl] = useState('');
  const [typedText, setTypedText] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [timeLimit, setTimeLimit] = useState(0);
  const [dictationWpm, setDictationWpm] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [wordCount, setWordCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [testType, setTestType] = useState('');
  const [canTakeTest, setCanTakeTest] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [selectedSpeed, setSelectedSpeed] = useState(0);
  const [showSpeedOptions, setShowSpeedOptions] = useState(false);
  const [source, setSource] = useState('');

  // Speed options from -15 to +50 in increments of 5
  const speedOptions = [-15, -10, -5, 0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50];

  // Load saved speed from localStorage
  useEffect(() => {
    const savedSpeed = localStorage.getItem('dictationSpeed');
    if (savedSpeed) {
      setSelectedSpeed(parseInt(savedSpeed));
    }
  }, []);


  // Fetch test metadata and audio
  useEffect(() => {
    const fetchTest = async () => {
      try {
        const { data } = await api.get(`/tests/${id}`);
        if (data.testType === 'premium' && (!user || !user.isPremium)) {
          setCanTakeTest(false);
        } else {
          setCanTakeTest(true);
        }

        setAudioUrl(`data:${data.contentType};base64,${data.audio}`);
        setName(data.name);
        setCategory(data.category);
        setTimeLimit(data.timeLimit);
        setDictationWpm(data.dictationWpm || 0);
        setAudioDuration(data.audioDuration || 0);
        setTestType(data.testType);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchTest();
  }, [id, user, navigate]);

  // Auto-play audio when loaded
  useEffect(() => {
    if (audioUrl && audioRef.current) {
      audioRef.current.load();
      // Apply the selected speed to the audio
      const playbackRate = 1 + (selectedSpeed / 100);
      audioRef.current.playbackRate = playbackRate;
      audioRef.current.play().catch(console.error);
    }
  }, [audioUrl, selectedSpeed]);
  useEffect(() => {
    if (!timerActive || timeLimit <= 0 || submitted) return;

    const timer = setInterval(() => {
      setElapsed(prev => {
        const newElapsed = prev + 1;

        if (newElapsed >= timeLimit) {
          clearInterval(timer);
          setTimerActive(false);
          handleSubmit(); // ✅ submit only when timer finishes
          return timeLimit; 
        }

        return newElapsed;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timerActive, timeLimit, submitted]);


  const handleEnded = () => {
    setTimerActive(true);
  };

  const handleSkip = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setTimerActive(true);
    }
  };

  const handleSpeedSelect = (speed) => {
    setSelectedSpeed(speed);
    setShowSpeedOptions(false);
    // Save the selected speed
    localStorage.setItem('dictationSpeed', speed.toString());
    // Apply the speed to the audio
    if (audioRef.current) {
      const playbackRate = 1 + (speed / 100);
      audioRef.current.playbackRate = playbackRate;
    }
  };

  const getEffectiveSpeed = () => {
    return dictationWpm + selectedSpeed;
  };

  const handleChangeSpeed = () => {
    navigate(`/speed-selection/${id}`);
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (submitted) return;

    let finalTypedText = typedText;
    try {
      if (editorRef.current && editorRef.current.editor) {
        finalTypedText = editorRef.current.editor.getContent();
      }
    } catch (err) {
      console.error('Error getting editor content:', err);
    }

    console.log('Preparing to submit test:', { typedText: finalTypedText });
    try {
      setSubmitted(true);
      console.log('Submitting test:', { typedText: finalTypedText, timeTaken: elapsed });
      await api.post(`/tests/${id}/submit`, {
        typedText: finalTypedText.trim(),
        timeTaken: elapsed
      });
      navigate(`/tests/${id}/results`);
    } catch (err) {
      console.error('Submission error:', err);
      alert('Submission failed. Please try again.');
      setSubmitted(false); // Allow retry on failure
    }
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #667eea',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          animation: 'spin 1s linear infinite',
          margin: '2rem auto'
        }}></div>
      </div>
    );
  }

  return (
    <>
      {!timerActive ? (
        <div style={containerStyle}>
          <div style={cardStyle}>
            <div style={headerStyle}>
              <h2 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>{name}</h2>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: '1rem' }}>
                {testType === 'premium' && (
                  <span style={{ backgroundColor: '#8e44ad', color: '#fff', padding: '0.5rem 1rem', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' }}>PREMIUM</span>
                )}
                {testType === 'simple' && (
                  <span style={{ backgroundColor: '#28a745', color: '#fff', padding: '0.5rem 1rem', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' }}>FREE</span>
                )}
                {testType === 'go-live' && (
                  <span style={{ backgroundColor: '#e74c3c', color: '#fff', padding: '0.5rem 1rem', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' }}>GO-LIVE</span>
                )}
              </div>
              {testType === 'premium' && !canTakeTest && (
                <div style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem',
                  color: '#856404'
                }}>
                  <strong>Premium Test:</strong> This is a premium test. Please upgrade to a premium subscription to take this test.
                </div>
              )}
            </div>
            <audio
              ref={audioRef}
              src={audioUrl}
              style={audioStyle}
              autoPlay
              onEnded={handleEnded}
            />
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1rem' }}>
              {canTakeTest ? (
                <button
                style={buttonStyle}
                onClick={handleSkip}
                onMouseEnter={e => {
                  e.target.style.backgroundColor = '#5a67d8';
                  e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={e => {
                  e.target.style.backgroundColor = '#667eea';
                  e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                }}
              >
                Skip and Start Typing
              </button>
              ) : (
                <button
                  style={{ ...buttonStyle, backgroundColor: '#6c757d', cursor: 'not-allowed' }}
                  disabled
                >
                  Upgrade to Take Test
                </button>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginTop: '1.5rem', fontSize: '14px' }}>
              <p><strong>Category:</strong> {category.toUpperCase()}</p>
              <p><strong>Time Limit:</strong> {timeLimit}s</p>
              <p><strong>Dictation Speed:</strong>{ getEffectiveSpeed() } WPM</p>
              <p><strong>Word Count:</strong> {wordCount}</p>
            </div>
          </div>
        </div>
      ) : (
        <div style={overlayStyle}>


          <div style={{ textAlign: 'center', marginBottom: '1rem', marginTop: '1rem', fontSize: '16px', fontWeight: 'bold', color: '#2c3e50' }}>
            Timer: {Math.floor(elapsed / 60)}m {elapsed % 60}s / {Math.floor(timeLimit / 60)}m {timeLimit % 60}s
          </div>

          {!submitted && (
            <button
              type="button"
              style={{
                ...buttonStyle,
                backgroundColor: elapsed >= timeLimit ? '#6c757d' : '#e74c3c',
                padding: '0.75rem 1.5rem',
                fontSize: '14px',
                marginBottom: '1.5rem',
                marginTop: '1rem',
                cursor: elapsed >= timeLimit ? 'not-allowed' : 'pointer'
              }}
              onClick={handleSubmit}
              disabled={elapsed >= timeLimit}
              onMouseEnter={e => {
                if (elapsed < timeLimit) {
                  e.target.style.backgroundColor = '#c0392b';
                  e.target.style.boxShadow = '0 6px 16px rgba(231, 76, 60, 0.4)';
                }
              }}
              onMouseLeave={e => {
                if (elapsed < timeLimit) {
                  e.target.style.backgroundColor = '#e74c3c';
                  e.target.style.boxShadow = '0 4px 12px rgba(231, 76, 60, 0.3)';
                }
              }}
            >
              {elapsed >= timeLimit ? 'Submitting...' : 'Submit (जमा करें)'}
            </button>
          )}

          {submitted && (
            <div style={{
              backgroundColor: '#d4edda',
              border: '1px solid #c3e6cb',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem',
              marginTop: '1rem',
              color: '#155724',
              textAlign: 'center'
            }}>
              <strong>Test submitted successfully!</strong> Redirecting to results...
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ width: '100%', margin: '0 auto', marginBottom: '4rem', marginTop: '1rem' }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              padding: '1.5rem',
              border: '1px solid #e9ecef',
              margin: '0 1rem'
            }}>
              <Editor
                ref={editorRef}
                tinymceScriptSrc='/tinymce/tinymce.min.js'
                licenseKey='gpl'
                value={typedText}
                onEditorChange={(content) => setTypedText(content)}
                disabled={submitted || elapsed >= timeLimit}
                init={{
                  height: 350,
                  menubar: false,
                  plugins: 'lists',
                  toolbar: 'undo redo',
                  font_family_formats: `
                    Kruti Dev 010=Kruti Dev 010;
                    DevLys 010=DevLys 010;
                  `,
                  content_style: `
                    @font-face { font-family: 'Kruti Dev 010'; src: url('/fonts/KrutiDev_010.ttf') format('truetype'); }
                    @font-face { font-family: 'DevLys 010'; src: url('/fonts/DevLys_010.ttf') format('truetype'); }
                    body { font-family: 'Kruti Dev 010', monospace; font-size: 24px; margin: 8px; line-height: 1.4;}
                  `,
                  readonly: submitted || elapsed >= timeLimit,
                  forced_root_block: 'p',
                  force_p_newlines: true,
                  forced_br_newlines: false,
                  convert_newlines_to_brs: false,
                  remove_linebreaks: false,
                  entities: 'raw',
                  entity_encoding: 'raw',
                  valid_elements: 'p,br,strong,em,span[style],font[face,size]',
                  invalid_elements: 'div,table,thead,tbody,tfoot,tr,td,th,h1,h2,h3,h4,h5,h6,ul,ol,li,blockquote,pre,a,img',
                  schema: 'html5',
                  end_container_on_empty_block: false,
                  extended_valid_elements: 'p[style]',
                  custom_elements: '~b',
                  valid_children: '+p[br]'
                }}
              />
            </div>
            <div style={{
              textAlign: 'right',
              marginTop: '1rem',
              fontSize: '16px',
              color: '#667eea',
              fontWeight: 'bold',
              marginRight: '2rem'
            }}>
              Words Typed: {typedText.trim().split(/\s+/).filter(w => w).length}
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default TestDetail;
